import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import TableAndPagination from '../../../../../../_common/TableAndPagination/TableAndPagination';
import Modal from '../../../../../../_common/Modal/Modal';
import Loader from '../../../../../../_common/Loader/Loader';
import { formattedCount, formattedDate } from '../../../pipelineHelpers';

const tableHeader = [
  { name: 'Patient', path: 'name' },
  { name: 'Date of birth', path: 'dob' },
  { name: 'Phone number', path: 'phone' },
  { name: 'Address', path: 'address' },
];

const initialState = {
  idsLoaded: false,
  firstLoad: true,
  loading: false,
  patientIds: [],
  total: 0,
  list: [],
  page: 1,
  limit: 15,
};

const formattedPatients = (list) => (list || []).map((d) => ({
  ...d,
  name: [d.firstName, d.lastName].filter(Boolean).join(' ') || '-',
  dob: formattedDate(d.dob),
  address: _.compact([d.addressStreet, d.addressCity, d.addressState, d.addressZipCode]).join(', '),
}));

const GapDaysDistributionModal = ({
  isOpen, params, bucket, onClose,
}) => {
  const navigate = useNavigate();

  const [patients, setPatients] = useState(initialState);

  const {
    idsLoaded, patientIds, page, limit,
  } = patients;

  useEffect(() => {
    if (!isOpen) {
      setPatients(initialState);

      return;
    }

    (async () => {
      try {
        setPatients((prev) => ({ ...prev, loading: true }));

        const { data } = await Api.getGapDaysDistributionPatients({ ...params, bucket: bucket.key });

        setPatients((prev) => ({
          ...prev,
          idsLoaded: true,
          patientIds: data?.data?.patientIds || [],
          total: data?.data?.total || 0,
          page: 1,
        }));
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [isOpen, params, bucket.key]);

  useEffect(() => {
    if (!isOpen || !idsLoaded) return;

    const pageIds = patientIds.slice((page - 1) * limit, page * limit);

    if (!pageIds.length) {
      setPatients((prev) => ({
        ...prev, list: [], loading: false, firstLoad: false,
      }));

      return;
    }

    (async () => {
      try {
        setPatients((prev) => ({ ...prev, loading: true }));

        const { data } = await Api.getPatients({ ids: pageIds.join(','), limit, page: 1 });

        setPatients((prev) => ({
          ...prev,
          list: formattedPatients(data?.data?.response),
          loading: false,
          firstLoad: false,
        }));
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [isOpen, idsLoaded, patientIds, page, limit]);

  const onChangePageData = (path, value) => {
    setPatients((prev) => (path === 'limit'
      ? { ...prev, limit: value, page: 1 }
      : { ...prev, page: value }));
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className={classes.members_modal}
    >
      <div className={classes.members_modal_content}>
        <h2>Patients in this band</h2>

        {!patients.firstLoad && (
          <div className={classes.members_modal_caption}>
            {bucket.label}

            <span>{`${formattedCount(patients.total)} members`}</span>
          </div>
        )}

        <div className={classes.members_modal_table}>
          {patients.firstLoad
            ? (
              <div className={classes.members_modal_loader}>
                <Loader />
              </div>
            )

            : (
              <TableAndPagination
                header={tableHeader}
                data={patients.list}
                loading={patients.loading}
                onChange={onChangePageData}
                className={classes.clickable_row}
                onClick={(PatientId) => navigate(`/patients/${PatientId}`)}
                totalPage={Math.ceil(patientIds.length / limit) || 1}
                page={page - 1}
                limit={limit}
              />
            )}
        </div>
      </div>
    </Modal>
  );
};

GapDaysDistributionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  params: PropTypes.object.isRequired,
  bucket: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default GapDaysDistributionModal;
